/**
 * ScopeLabel -- the one-line "what do these numbers count?" caption on summary tiles (OP#1787).
 *
 * Wording comes from lib/tileScope (one rule for every tile). This component only renders it
 * on the design-system tokens: `text-ink-subtle` on whatever card ground it sits on, so it
 * reads correctly in light and dark without its own colours, and truncates rather than
 * wrapping a tile taller than its neighbours. The full sentence is the `title` tooltip; the
 * short text is the accessible description (tiles reference the id with aria-describedby).
 *
 * useScopeNames() resolves ids to the names the operator already sees in the ScopeBar
 * (quarantine org labelled, never shown by its stored name -- lib/quarantine.orgLabel).
 */
import { useSession } from "@/stores/session";
import { orgLabel } from "@/lib/quarantine";
import type { ScopeLabel as ScopeLabelData, ScopeNames } from "@/lib/tileScope";
import { cn } from "@/lib/cn";

export function useScopeNames(): ScopeNames {
  const me = useSession((s) => s.me);
  return {
    org: (id) => orgLabel(me?.orgs.find((o) => String(o.id) === id) ?? null),
    site: (id) => me?.sites.find((s) => String(s.id) === id)?.name,
  };
}

export function ScopeLabel({
  label,
  id,
  className,
}: {
  label: ScopeLabelData | null;
  /** Referenced by the tile(s) it describes (aria-describedby). */
  id?: string;
  className?: string;
}) {
  if (!label) return null;
  return (
    <div
      id={id}
      data-testid="tile-scope"
      title={label.title}
      className={cn("flex min-w-0 items-center gap-1 text-[0.6875rem] text-ink-subtle", className)}
    >
      {/* Crosshair glyph: "this is the scope", distinct from the status ramp colours. */}
      <svg aria-hidden viewBox="0 0 12 12" className="h-3 w-3 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.2">
        <circle cx="6" cy="6" r="3.5" />
        <path d="M6 0.5v2.5M6 9v2.5M0.5 6H3M9 6h2.5" />
      </svg>
      <span className="truncate">{label.text}</span>
    </div>
  );
}
